import { useState, useCallback } from "react";
import { updateCartQuantity, dispatchCartUpdates } from "@/shared/utils/cartOperations";
import { getCartQuantityForVariant, getTargetCartItem, isVariantInCart } from "../utils/cartHelpers";
import { getTrackTypeFromKey } from "../utils/trackHelpers";
import { TTrackTypes } from "@/types";

interface UseEditModeProps {
  cartData: any[];
  productRenderedLocation: string;
  headlessCartInfo: any;
  membershipInfo: any;
  isCustomerLogged: boolean;
  isCustomerActiveSubscriber: boolean;
  dispatch: any;
  trackSelections: {
    mainTrackSelection: any;
    battleKartSelection: any;
    miniTrackSection: any;
    juniorTrackSelection: any;
    intermediateTrackSelection: any;
  };
}

export const useEditMode = ({
  cartData,
  productRenderedLocation,
  headlessCartInfo,
  membershipInfo,
  isCustomerLogged,
  isCustomerActiveSubscriber,
  dispatch,
  trackSelections,
}: UseEditModeProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTrackKey, setEditingTrackKey] = useState<string | null>(null);
  const [editSessionTime, setEditSessionTime] = useState("");
  const [editPlayerCount, setEditPlayerCount] = useState(1);
  const [originalPlayerCount, setOriginalPlayerCount] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  // Track edited quantities for sessions not yet in cart (variantId -> quantity)
  const [editedQuantities, setEditedQuantities] = useState<Record<string, number>>({});

  const handleEditClick = useCallback(
    (trackKey: string, firstSessionId?: string) => {
      setIsEditMode(true);
      setEditingTrackKey(trackKey);
      setEditSessionTime(firstSessionId || "");
      if (firstSessionId) {
        // Check if this session is in the cart
        const isInCart = isVariantInCart(firstSessionId, cartData, productRenderedLocation);
        
        if (isInCart) {
          // Use cart quantity for items in cart
          const actualPlayerCount = getCartQuantityForVariant(firstSessionId, cartData, productRenderedLocation);
          setEditPlayerCount(actualPlayerCount);
          setOriginalPlayerCount(actualPlayerCount);
        } else {
          // For items not in cart, use edited quantity if available, otherwise default to 1
          const existingEditedQty = editedQuantities[firstSessionId];
          const playerCount = existingEditedQty || 1;
          setEditPlayerCount(playerCount);
          setOriginalPlayerCount(playerCount);
        }
      } else {
        setEditPlayerCount(1);
        setOriginalPlayerCount(1);
      }
    },
    [cartData, productRenderedLocation, editedQuantities]
  );

  // Check if a variant is currently in the cart
  const checkIsVariantInCart = useCallback(
    (variantId: string) => {
      return isVariantInCart(variantId, cartData, productRenderedLocation);
    },
    [cartData, productRenderedLocation]
  );

  const updateCartQuantityForSession = useCallback(
    async (variantId: string, newQuantity: number) => {
      const targetItem = getTargetCartItem(variantId, cartData, productRenderedLocation);
      if (!targetItem) {
        throw new Error(`Cart item not found for variant: ${variantId}`);
      }

      const trackType: TTrackTypes | null = editingTrackKey ? getTrackTypeFromKey(editingTrackKey) : null;
      let selectedVariants = null;

      if (trackType) {
        switch (trackType) {
          case "MAIN_TRACK":
            selectedVariants = trackSelections.mainTrackSelection.variantsAndDate;
            break;
          case "BATTLE_KART_TRACK":
            selectedVariants = trackSelections.battleKartSelection.variantsAndDate;
            break;
          case "MINI_TRACK":
            selectedVariants = trackSelections.miniTrackSection.variantsAndDate;
            break;
          case "JUNIOR_TRACK":
            selectedVariants = trackSelections.juniorTrackSelection.variantsAndDate;
            break;
          case "ARCADE":
            // Arcade doesn't use variantsAndDate, quantity is updated directly in cart
            selectedVariants = null;
            break;
        }
      }

      const result = await updateCartQuantity(
        [targetItem.cartItemId],
        newQuantity,
        productRenderedLocation,
        headlessCartInfo,
        trackType,
        selectedVariants,
        dispatch
      );

      dispatchCartUpdates(dispatch, result.cart, productRenderedLocation, membershipInfo, {
        isCustomerLogged,
        isCustomerActiveSubscriber,
      });

      return result;
    },
    [
      cartData,
      productRenderedLocation,
      editingTrackKey,
      trackSelections,
      headlessCartInfo,
      dispatch,
      membershipInfo,
      isCustomerLogged,
      isCustomerActiveSubscriber,
    ]
  );

  // Save changes - update cart for cart items, store edited quantity for non-cart items
  const handleSaveChanges = useCallback(async () => {
    if (!editSessionTime) {
      setIsEditMode(false);
      setEditingTrackKey(null);
      return;
    }

    // Only process if the quantity actually changed
    if (editPlayerCount !== originalPlayerCount) {
      // Check if the item is in the cart
      const isInCart = isVariantInCart(editSessionTime, cartData, productRenderedLocation);

      if (isInCart) {
        // Item is in cart - update cart quantity
      setIsSaving(true);
      try {
        await updateCartQuantityForSession(editSessionTime, editPlayerCount);
        setOriginalPlayerCount(editPlayerCount);
      } catch (error) {
          console.error("Failed to save changes:", error);
          // Revert to original on error
          setEditPlayerCount(originalPlayerCount);
        } finally {
          setIsSaving(false);
        }
      } else {
        // Item is NOT in cart - store the edited quantity for later use when adding to cart
        setEditedQuantities(prev => ({
          ...prev,
          [editSessionTime]: editPlayerCount
        }));
        setOriginalPlayerCount(editPlayerCount);
      }
    }

    setIsEditMode(false);
    setEditingTrackKey(null);
  }, [editSessionTime, editPlayerCount, originalPlayerCount, updateCartQuantityForSession, cartData, productRenderedLocation]);

  // Cancel edit - revert to original player count
  const handleCancelEdit = useCallback(() => {
    setEditPlayerCount(originalPlayerCount);
    setIsEditMode(false);
    setEditingTrackKey(null);
  }, [originalPlayerCount]);

  // Increment players locally (cart update happens on save)
  const incrementPlayers = useCallback(() => {
    setEditPlayerCount((prev) => prev + 1);
  }, []);

  // Decrement players locally (cart update happens on save)
  const decrementPlayers = useCallback(() => {
    setEditPlayerCount((prev) => (prev > 1 ? prev - 1 : prev));
  }, []);

  // Get the quantity for a session - prioritizes: cart quantity > edited quantity > default
  const getSessionQuantity = useCallback(
    (variantId: string, defaultQuantity: number = 1) => {
      // First check if it's in cart
      if (isVariantInCart(variantId, cartData, productRenderedLocation)) {
        return getCartQuantityForVariant(variantId, cartData, productRenderedLocation);
      }
      // Then check if it has an edited quantity
      if (editedQuantities[variantId]) {
        return editedQuantities[variantId];
      }
      // Fall back to default
      return defaultQuantity;
    },
    [cartData, productRenderedLocation, editedQuantities]
  );

  // Clear edited quantities (call after adding to cart)
  const clearEditedQuantities = useCallback((variantIds?: string[]) => {
    if (variantIds) {
      // Clear specific variants
      setEditedQuantities(prev => {
        const updated = { ...prev };
        variantIds.forEach(id => delete updated[id]);
        return updated;
      });
    } else {
      // Clear all
      setEditedQuantities({});
    }
  }, []);

  return {
    isEditMode,
    editingTrackKey,
    editSessionTime,
    editPlayerCount,
    isSaving,
    editedQuantities,
    setEditSessionTime,
    setEditPlayerCount,
    handleEditClick,
    handleSaveChanges,
    handleCancelEdit,
    incrementPlayers,
    decrementPlayers,
    checkIsVariantInCart,
    getSessionQuantity,
    clearEditedQuantities,
  };
};

